import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
// D10 — shared shell replaces the previous Dialog/DialogContent/DialogHeader
// wrapping. AlertDialog companion (cancel-confirm) + CreateSubscriptionInvoiceDialog
// stay as siblings — Radix supports nested Dialog instances via context.
import { BookingDetailShell } from "@/components/admin/schedule/BookingDetailShell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, Pause, Play, Save, X, FileText, CalendarOff, Undo2 } from "lucide-react";
import { CreateSubscriptionInvoiceDialog } from "./CreateSubscriptionInvoiceDialog";
import { cn } from "@/lib/utils";
// Bug 5 fix — release/undo actions extracted to a tested helper module.
import { performRelease, performUndoRelease } from "@/lib/subscriptionReleaseActions";
// Spår S1 — split-pattern plan-builder for D11-compliant edit semantics.
import {
  buildSubscriptionSplitPlan,
  type SubscriptionRow,
} from "@/lib/subscriptionSplitPlan";

const DAY_NAMES = ["Sö", "Må", "Ti", "On", "To", "Fr", "Lö"];

interface SubscriptionDetailDialogProps {
  subscription: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courts: { id: string; name: string }[];
  onUpdated: () => void;
  customerName?: string;
  organizationId?: string;
  selectedDate?: Date;
}

export function SubscriptionDetailDialog({
  subscription,
  open,
  onOpenChange,
  courts,
  onUpdated,
  customerName,
  organizationId,
  selectedDate,
}: SubscriptionDetailDialogProps) {
  const { t } = useTranslation("admin");
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAlreadyReleased, setIsAlreadyReleased] = useState(false);

  // Check if this occurrence is already released
  useEffect(() => {
    if (!selectedDate || !subscription.id) return;
    const checkRelease = async () => {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const { data } = await supabase
        .from("subscription_releases" as any)
        .select("id")
        .eq("subscription_id", subscription.id)
        .eq("release_date", dateStr)
        .eq("status", "released")
        .maybeSingle();
      setIsAlreadyReleased(!!data);
    };
    checkRelease();
  }, [subscription.id, selectedDate]);

  const handleRelease = async () => {
    if (!selectedDate || !organizationId) return;
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const { error } = await performRelease(supabase as any, {
      subscriptionId: subscription.id,
      releaseDate: dateStr,
      courtId: subscription.court_id,
      startTime: subscription.start_time,
      endTime: subscription.end_time,
      organizationId,
      releasedBy: user?.id,
      originalPrice: subscription.price || 0,
    });
    if (error) {
      if (error.code === "23505") {
        // Bug 5 defensive: after the DELETE-on-undo fix this branch should
        // not fire in practice. Kept so legacy cancelled tombstones still
        // surface a user-friendly message until the cleanup migration runs.
        toast.error(t("subscription.alreadyReleasedToast"));
      } else {
        toast.error(t("subscription.releaseError") + ": " + error.message);
      }
    } else {
      toast.success(t("subscription.releaseSuccess", { date: dateStr }));
      setIsAlreadyReleased(true);
      onUpdated();
    }
  };

  const handleUndoRelease = async () => {
    if (!selectedDate) return;
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    // Bug 5 fix — DELETE not UPDATE; see performUndoRelease in
    // src/lib/subscriptionReleaseActions.ts for the rationale.
    const { error } = await performUndoRelease(supabase as any, {
      subscriptionId: subscription.id,
      releaseDate: dateStr,
    });
    if (error) {
      toast.error(t("subscription.releaseError") + ": " + error.message);
      return;
    }
    toast.success(t("subscription.releaseUndone"));
    setIsAlreadyReleased(false);
    onUpdated();
  };

  const [editCourtId, setEditCourtId] = useState(subscription.court_id ?? "");
  const [editDayOfWeek, setEditDayOfWeek] = useState<number>(subscription.day_of_week ?? 0);
  const [editStartTime, setEditStartTime] = useState(subscription.start_time?.slice(0, 5) ?? "");
  const [editEndTime, setEditEndTime] = useState(subscription.end_time?.slice(0, 5) ?? "");
  // Spår S1 — Beslut C: "Giltig från"-fältet är split-datumet, default IDAG
  // (inte subscription.valid_from). Operator kan välja framåtdatum för att
  // staga ändringen — pris/värden från gamla raden gäller fram till valt
  // datum, nya från och med. Abonnemanget är obrutet över gränsen.
  const todayIso = new Date().toISOString().slice(0, 10);
  const [editValidFrom, setEditValidFrom] = useState(todayIso);
  const [editValidUntil, setEditValidUntil] = useState(subscription.valid_until ?? "");
  const [editInterval, setEditInterval] = useState(String(subscription.interval_weeks ?? 1));
  const [editPrice, setEditPrice] = useState(String(subscription.price ?? 0));
  const [editPaymentMethod, setEditPaymentMethod] = useState(subscription.payment_method ?? "invoice");
  const [editComment, setEditComment] = useState(subscription.comment ?? "");

  const startTime = subscription.start_time?.slice(0, 5) ?? "";
  const endTime = subscription.end_time?.slice(0, 5) ?? "";
  const courtName = courts.find((c) => c.id === subscription.court_id)?.name ?? t("subscription.court");
  const displayName = customerName || t("subscription.title");

  const resetEditState = () => {
    setEditCourtId(subscription.court_id ?? "");
    setEditDayOfWeek(subscription.day_of_week ?? 0);
    setEditStartTime(subscription.start_time?.slice(0, 5) ?? "");
    setEditEndTime(subscription.end_time?.slice(0, 5) ?? "");
    // Beslut C — pre-fill split-date with today, not subscription.valid_from
    setEditValidFrom(todayIso);
    setEditValidUntil(subscription.valid_until ?? "");
    setEditInterval(String(subscription.interval_weeks ?? 1));
    setEditPrice(String(subscription.price ?? 0));
    setEditPaymentMethod(subscription.payment_method ?? "invoice");
    setEditComment(subscription.comment ?? "");
  };

  // Spår S1 — Beslut D: redigering tillåten på status IN ('active','paused').
  // Cancelled-abonnemang förblir oredigerbara (befintligt beteende).
  const canEditSubscription =
    subscription.status === "active" || subscription.status === "paused";

  // Spår S1 — split-pattern handleSave. Builds plan via pure helper,
  // calls split_recurring_booking RPC for atomic transaction, logs audit
  // event. No-op short-circuits with info-toast + close.
  const handleSave = async () => {
    if (!canEditSubscription) {
      toast.error(t("subscription.cannotEditCancelled", { defaultValue: "Avbokade abonnemang kan inte redigeras." }));
      return;
    }
    setSaving(true);
    try {
      // Normalize HH:mm → HH:mm:ss for time fields
      const normalizedStart = editStartTime.length === 5 ? editStartTime + ":00" : editStartTime;
      const normalizedEnd   = editEndTime.length   === 5 ? editEndTime   + ":00" : editEndTime;
      const priceNum = parseFloat(editPrice) || 0;
      const intervalNum = parseInt(editInterval) || 1;
      const commentNorm = (editComment ?? "").length === 0 ? null : editComment;

      // Build the split plan. `today` is injected for testability via the
      // helper, but here at the call-site we use real today since the
      // operator-facing UI reflects calendar time.
      const plan = buildSubscriptionSplitPlan({
        oldRow: subscription as SubscriptionRow,
        edits: {
          court_id: editCourtId,
          day_of_week: editDayOfWeek,
          start_time: normalizedStart,
          end_time: normalizedEnd,
          valid_from: editValidFrom,          // Beslut C — operator's chosen split-date
          valid_until: editValidUntil,
          interval_weeks: intervalNum,
          price: priceNum,
          payment_method: editPaymentMethod,
          comment: commentNorm,
        },
        today: todayIso,
      });

      if (plan.noop) {
        toast.info(t("subscription.noChanges", { defaultValue: "Inga ändringar att spara." }));
        setEditing(false);
        return;
      }

      // S1-fix (D12): RPC handles both 'inplace' (same id) and 'split'
      // (new id). Client passes identical args; RPC decides internally
      // based on _new_valid_from = v_old.valid_from. Plan-helper computes
      // the mode for audit-log clarity.
      const rpcFields = plan.mode === "inplace" ? plan.update : plan.newInsert;
      const { data: returnedIdRaw, error } = await supabase.rpc(
        "split_recurring_booking" as never,
        {
          _old_id: subscription.id,
          _new_valid_from: plan.splitDate,
          _new_court_id: rpcFields.court_id,
          _new_day_of_week: rpcFields.day_of_week,
          _new_start_time: rpcFields.start_time,
          _new_end_time: rpcFields.end_time,
          _new_valid_until: rpcFields.valid_until,
          _new_interval_weeks: rpcFields.interval_weeks ?? 1,
          _new_price: rpcFields.price ?? 0,
          _new_price_minor: rpcFields.price_minor ?? 0,
          _new_payment_method: rpcFields.payment_method ?? "invoice",
          _new_comment: rpcFields.comment,
        } as never,
      );

      if (error) {
        toast.error(t("subscription.saveError") + ": " + (error.message ?? String(error)));
        return;
      }

      // Audit log — Beslut E action name `subscription_split_edit`.
      // RPC returns: same id for inplace, new id for split. Metadata
      // surfaces mode so future-CC (or operator audit reports) can
      // distinguish in-place edits from splits.
      //
      // BUG-A fix (PR #290, 2026-05-19): tidigare INSERT mot
      // `booking_activity_log` slog tyst i golvet eftersom dess
      // `booking_id`-kolumn är FK till `bookings(id)`, inte
      // `recurring_bookings(id)`. Nu skriver vi till dedikerad
      // `subscription_activity_log` med FK mot recurring_bookings.
      // Vid 'split' loggar vi BARA mot gamla raden (subscription.id) —
      // den nya raden (returnedId) syns via metadata.new_subscription_id.
      // Detta håller audit-spåret kronologiskt per "ursprunglig serie".
      const returnedId = typeof returnedIdRaw === "string" ? returnedIdRaw : null;
      const fieldsChanged = Object.keys(rpcFields).filter((k) => {
        if (k === "id") return false;
        const before = (subscription as Record<string, unknown>)[k];
        const after = (rpcFields as Record<string, unknown>)[k];
        return before !== after;
      });
      const { error: auditError } = await supabase
        .from("subscription_activity_log" as never)
        .insert({
          subscription_id: subscription.id,
          organization_id: subscription.organization_id,
          action: "subscription_split_edit",
          actor_user_id: user?.id ?? null,
          metadata: {
            mode: plan.mode,                                 // 'inplace' | 'split'
            old_subscription_id: subscription.id,
            // For inplace: returned_id === old id (no new row). For split:
            // returned_id is the new row's id.
            returned_id: returnedId,
            new_subscription_id: plan.mode === "split" ? returnedId : null,
            split_date: plan.splitDate,
            old_valid_until: plan.mode === "split" ? plan.oldUpdate.valid_until : null,
            fields_changed: fieldsChanged,
          },
        } as never);
      if (auditError) {
        // Audit-fel blockerar ej save (RPC har redan committat). Logga
        // som error (inte warn) så det syns i Sentry/console. Operator
        // ser fortfarande success-toasten — viktigare att inte ge falsk
        // "save failed" på en ren audit-skrivning.
        console.error(
          "[subscription_split_edit] audit log insert failed:",
          auditError.message,
        );
      }

      toast.success(t("subscription.updated"));
      setEditing(false);
      onUpdated();
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    const { error } = await supabase
      .from("recurring_bookings" as any)
      .update({ status: newStatus })
      .eq("id", subscription.id);
    if (error) {
      toast.error(t("subscription.updateError") + ": " + (error.message ?? String(error)));
    } else {
      toast.success(
        newStatus === "paused" ? t("subscription.paused_toast")
          : newStatus === "active" ? t("subscription.activated_toast")
          : t("subscription.cancelled_toast")
      );
      onOpenChange(false);
      onUpdated();
    }
  };

  const intervalLabel = (weeks: number) => {
    if (weeks === 1) return t("subscription.everyWeek");
    if (weeks === 2) return t("subscription.everyOtherWeek");
    return t("subscription.everyNWeeks", { n: weeks });
  };

  const paymentLabel = (method: string) => {
    const map: Record<string, string> = {
      invoice: t("subscription.invoice"),
      free: t("subscription.free"),
      paid_cash_swish: t("subscription.paidCashSwish"),
      credits: t("subscription.credits"),
      customer_pays: t("subscription.customerPays"),
      unpaid: t("subscription.unpaid"),
    };
    return map[method] ?? method;
  };

  return (
    <>
      <BookingDetailShell
        open={open}
        onOpenChange={onOpenChange}
        title={editing ? t("subscription.editSubscription") : displayName}
        subtitle={<>{startTime}–{endTime} · {courtName} · {DAY_NAMES[subscription.day_of_week ?? 0]}</>}
      >
          {editing ? (
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-xs">{t("subscription.court")}</Label>
                <Select value={editCourtId} onValueChange={setEditCourtId}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {courts.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("subscription.weekday")}</Label>
                <div className="flex gap-1.5 mt-1">
                  {DAY_NAMES.map((name, i) => (
                    <button key={i} type="button" onClick={() => setEditDayOfWeek(i)}
                      className={cn("w-8 h-8 rounded-md text-xs font-medium border transition-colors",
                        editDayOfWeek === i ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:bg-accent")}>
                      {name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("subscription.startTime")}</Label>
                  <Input type="time" value={editStartTime} onChange={(e) => setEditStartTime(e.target.value)} className="h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">{t("subscription.endTime")}</Label>
                  <Input type="time" value={editEndTime} onChange={(e) => setEditEndTime(e.target.value)} className="h-9 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  {/* Spår S1 Beslut C — relabeled "Giltig från (ändring träder i kraft)"
                      to make split-date semantics explicit. Default today, editable forward. */}
                  <Label className="text-xs">{t("subscription.splitFromDate", { defaultValue: "Giltig från (ändringen träder i kraft)" })}</Label>
                  <Input
                    type="date"
                    value={editValidFrom}
                    min={todayIso}
                    max={editValidUntil || undefined}
                    onChange={(e) => setEditValidFrom(e.target.value)}
                    className="h-9 text-sm"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {t("subscription.splitFromDateHint", {
                      defaultValue: "Gamla värden gäller fram till dagen innan. Historiska instanser orörda.",
                    })}
                  </p>
                </div>
                <div>
                  <Label className="text-xs">{t("subscription.endDate")}</Label>
                  <Input type="date" value={editValidUntil} onChange={(e) => setEditValidUntil(e.target.value)} className="h-9 text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t("subscription.repeat")}</Label>
                <Select value={editInterval} onValueChange={setEditInterval}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">{t("subscription.everyWeek")}</SelectItem>
                    <SelectItem value="2">{t("subscription.everyOtherWeek")}</SelectItem>
                    <SelectItem value="3">{t("subscription.everyNWeeks", { n: 3 })}</SelectItem>
                    <SelectItem value="4">{t("subscription.everyNWeeks", { n: 4 })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("subscription.pricePerOccurrence")}</Label>
                <Input type="number" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">{t("subscription.payment")}</Label>
                <Select value={editPaymentMethod} onValueChange={setEditPaymentMethod}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">{t("subscription.invoice")}</SelectItem>
                    <SelectItem value="customer_pays">{t("subscription.customerPays")}</SelectItem>
                    <SelectItem value="paid_cash_swish">{t("subscription.paidCashSwish")}</SelectItem>
                    <SelectItem value="credits">{t("subscription.credits")}</SelectItem>
                    <SelectItem value="free">{t("subscription.free")}</SelectItem>
                    <SelectItem value="unpaid">{t("subscription.unpaid")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("subscription.comment")}</Label>
                <Input value={editComment} onChange={(e) => setEditComment(e.target.value)} placeholder={t("subscription.optionalComment")} className="h-9 text-sm" />
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  <Save className="h-3.5 w-3.5 mr-1" />
                  {saving ? t("subscription.saving") : t("subscription.save")}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  <X className="h-3.5 w-3.5 mr-1" />
                  {t("subscription.cancelBtn")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.customer")}</span>
                  <p className="font-medium">{displayName}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.status")}</span>
                  <p className="font-medium">
                    <Badge variant={subscription.status === "active" ? "default" : "secondary"} className="text-[10px]">
                      {subscription.status === "active" ? t("subscription.active") : subscription.status === "paused" ? t("subscription.paused") : t("subscription.cancelled")}
                    </Badge>
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.court")}</span>
                  <p className="font-medium">{courtName}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.weekday")}</span>
                  <p className="font-medium">{DAY_NAMES[subscription.day_of_week ?? 0]}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.time")}</span>
                  <p className="font-medium">{startTime}–{endTime}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.interval")}</span>
                  <p className="font-medium">{intervalLabel(subscription.interval_weeks ?? 1)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.validFrom")}</span>
                  <p className="font-medium">{subscription.valid_from}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.validUntil")}</span>
                  <p className="font-medium">{subscription.valid_until}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.price")}</span>
                  <p className="font-medium">{subscription.price ? `${subscription.price} kr` : t("subscription.free")}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.payment")}</span>
                  <p className="font-medium">{paymentLabel(subscription.payment_method)}</p>
                </div>
              </div>
              {subscription.comment && (
                <div>
                  <span className="text-muted-foreground text-xs">{t("subscription.comment")}</span>
                  <p className="text-sm">{subscription.comment}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {/* Spår S1 Beslut D — edit available for active+paused, hidden for cancelled */}
                {canEditSubscription && (
                  <Button size="sm" variant="outline" onClick={() => { resetEditState(); setEditing(true); }}>
                    <Pencil className="h-3.5 w-3.5 mr-1" />
                    {t("subscription.edit")}
                  </Button>
                )}
                {organizationId && (
                  <Button size="sm" variant="outline" onClick={() => setShowInvoiceDialog(true)}>
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    {t("subscription.createInvoice")}
                  </Button>
                )}
                {selectedDate && (
                  isAlreadyReleased ? (
                    <Button size="sm" variant="outline" onClick={handleUndoRelease}>
                      <Undo2 className="h-3.5 w-3.5 mr-1" />
                      {t("subscription.undoRelease")}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={handleRelease}>
                      <CalendarOff className="h-3.5 w-3.5 mr-1" />
                      {t("subscription.releaseThisWeek")}
                    </Button>
                  )
                )}
                {subscription.status === "active" ? (
                  <Button size="sm" variant="outline" onClick={() => handleStatusChange("paused")}>
                    <Pause className="h-3.5 w-3.5 mr-1" />
                    {t("subscription.pause")}
                  </Button>
                ) : subscription.status === "paused" ? (
                  <Button size="sm" variant="outline" onClick={() => handleStatusChange("active")}>
                    <Play className="h-3.5 w-3.5 mr-1" />
                    {t("subscription.activate")}
                  </Button>
                ) : null}
                <Button size="sm" variant="destructive" onClick={() => setConfirmCancel(true)}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  {t("subscription.cancel")}
                </Button>
              </div>
            </div>
          )}
      </BookingDetailShell>

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("subscription.cancelTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("subscription.cancelDescription")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("subscription.cancelBtn")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleStatusChange("cancelled")} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t("subscription.cancel")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showInvoiceDialog && organizationId && (
        <CreateSubscriptionInvoiceDialog
          subscription={subscription}
          open={showInvoiceDialog}
          onOpenChange={setShowInvoiceDialog}
          organizationId={organizationId}
          courtName={courts.find((c) => c.id === subscription.court_id)?.name ?? t("subscription.court")}
          customerName={customerName ?? t("subscription.customer")}
          onCreated={() => {
            setShowInvoiceDialog(false);
            onUpdated();
          }}
        />
      )}
    </>
  );
}
